import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CurrentUser } from './types';
import { api, authDriver, loginMicrosoft, logoutMicrosoft, setDevUserEmail } from './api';

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  driver: string;
  refresh: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  switchDevUser: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const driver = authDriver();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setUser(await api<CurrentUser>('/api/auth/me'));
    } catch (err) {
      setUser(null);
      setError(err instanceof Error ? err.message : 'No fue posible autenticar al usuario.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    const listener = () => void refresh();
    window.addEventListener('consultorio-auth-change', listener);
    return () => window.removeEventListener('consultorio-auth-change', listener);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      driver,
      refresh,
      login: async () => {
        await loginMicrosoft();
        await refresh();
      },
      logout: async () => {
        if (driver === 'entra') await logoutMicrosoft();
        setUser(null);
      },
      switchDevUser: async (email: string) => {
        setDevUserEmail(email);
        await refresh();
      },
    }),
    [user, loading, error, driver],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth debe utilizarse dentro de AuthProvider.');
  return value;
}
